import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiUsers, FiBell, FiLogOut, FiTruck, FiMenu, FiX, FiSettings, FiMessageSquare, FiList, FiClock, FiSearch, FiUser, FiCheck, FiAlertTriangle, FiMail } = FiIcons;

const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Load notifications from local storage (simulating backend)
  useEffect(() => {
    const loadNotifications = () => {
      const stored = JSON.parse(localStorage.getItem('adminNotifications') || '[]');
      // If empty, seed with some data
      if (stored.length === 0) {
        const initial = [
          { id: 1, title: 'Compliance Warning', message: 'Truck #42 (Rivera Transport) is due for inspection in 7 days.', type: 'warning', time: '10 min ago', read: false },
          { id: 2, title: 'System Update', message: 'Automated check completed successfully.', type: 'success', time: '1 hour ago', read: true },
        ];
        localStorage.setItem('adminNotifications', JSON.stringify(initial));
        setNotifications(initial);
      } else {
        setNotifications(stored);
      }
    };

    loadNotifications();
    // Poll for changes (to pick up alerts from Automations page)
    const interval = setInterval(loadNotifications, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  const handleLogout = () => {
    navigate('/admin/login');
  };

  const markAllRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    localStorage.setItem('adminNotifications', JSON.stringify(updated));
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: FiGrid },
    { path: '/admin/subscribers', label: 'Reminder List', icon: FiList },
    { path: '/admin/automations', label: 'Automations', icon: FiClock },
    { path: '/admin/email', label: 'Email Campaigns', icon: FiMail },
    { path: '/admin/sms', label: 'SMS Campaigns', icon: FiMessageSquare },
    { path: '/admin/leads', label: 'All Leads', icon: FiUsers },
    { path: '/admin/settings', label: 'Settings', icon: FiSettings },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-30 h-screen w-64 bg-gray-800 border-r border-gray-700 transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTruck} className="text-2xl text-blue-500" />
            <div>
              <span className="font-bold text-lg block leading-none">CPanel</span>
              <span className="text-xs text-gray-500 uppercase tracking-wider">Master Admin</span>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-white"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <nav className="mt-6 px-4 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50'
                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
            >
              <SafeIcon icon={item.icon} className="text-lg" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}

          <div className="pt-8 mt-8 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="text-lg" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Desktop Header */}
        <header className="bg-gray-800 border-b border-gray-700 p-4 hidden md:flex items-center justify-between">
          <div className="flex items-center gap-4 text-gray-400">
            <h2 className="text-white font-semibold text-lg">
              {menuItems.find(i => i.path === location.pathname)?.label || 'Admin Console'}
            </h2>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-2.5 text-gray-500" />
              <input
                type="text"
                placeholder="Quick Search..."
                className="bg-gray-900 border border-gray-700 rounded-full pl-10 pr-4 py-2 text-sm text-gray-300 focus:ring-2 focus:ring-blue-500 outline-none w-64"
              />
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiBell} className="text-xl" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-gray-800"></span>
                )}
              </button>

              {/* Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-3 w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in-up">
                  <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
                    <h3 className="font-bold text-white">Alerts</h3>
                    <button onClick={markAllRead} className="text-xs text-blue-400 hover:text-blue-300">Mark all read</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div key={notif.id} className={`p-4 border-b border-gray-700 hover:bg-gray-700/50 transition-colors ${!notif.read ? 'bg-blue-900/10' : ''}`}>
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-full shrink-0 ${notif.type === 'warning' ? 'bg-yellow-900/30 text-yellow-500' : 'bg-green-900/30 text-green-500'}`}>
                              <SafeIcon icon={notif.type === 'warning' ? FiAlertTriangle : FiCheck} className="text-sm" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{notif.title}</p>
                              <p className="text-xs text-gray-400 mt-1">{notif.message}</p>
                              <p className="text-[10px] text-gray-500 mt-2">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-gray-500 text-sm">No new notifications</div>
                    )}
                  </div>
                  <div className="p-3 bg-gray-900/50 text-center border-t border-gray-700">
                    <Link to="/admin/automations" onClick={() => setShowNotifications(false)} className="text-xs text-blue-400 hover:text-white">View Automation Logs</Link>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pl-6 border-l border-gray-700">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-medium text-white">Oscar Ramirez</p>
                <p className="text-xs text-blue-400">Administrator</p>
              </div>
              <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center border border-gray-600">
                <SafeIcon icon={FiUser} className="text-gray-300" />
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Header (Existing) */}
        <header className="bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-400 hover:text-white"
          >
            <SafeIcon icon={FiMenu} className="text-2xl" />
          </button>
          <span className="font-bold text-white">Ramirez Admin</span>
          <button className="text-gray-400 hover:text-white">
            <SafeIcon icon={FiBell} className="text-xl" />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-900">
          {children}
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;